import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Form,
  InputGroup,
  Badge,
  Modal,
  Tabs,
  Tab,
  Alert,
  Spinner,
} from "react-bootstrap";
import { FaEye, FaSearch, FaComment, FaTimes } from "react-icons/fa";
import api from "../api";
import MathJaxRenderer from "./MathJaxRenderer";

const SharedQuestions = () => {
  const [sharedQuestions, setSharedQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [subjects, setSubjects] = useState([]);

  // View question modal
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Comment modal
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    fetchSharedQuestions();
    fetchSubjects();
  }, []);

  const fetchSharedQuestions = async () => {
    try {
      setLoading(true);
      const response = await api.get("/api/shared-questions/");

      // Handle different response formats
      if (Array.isArray(response.data)) {
        setSharedQuestions(response.data);
      } else if (response.data && typeof response.data === "object") {
        if (Array.isArray(response.data.results)) {
          setSharedQuestions(response.data.results);
        } else {
          const questionsArray = Object.values(response.data);
          setSharedQuestions(
            Array.isArray(questionsArray) ? questionsArray : []
          );
        }
      } else {
        setSharedQuestions([]);
      }
    } catch (error) {
      console.error("Error fetching shared questions:", error);
      setError("Failed to load shared questions. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubjects = async () => {
    try {
      const response = await api.get("/api/csv-data/subjects/");
      if (Array.isArray(response.data)) {
        // Extract unique subjects from both shared questions and API
        const questionSubjects = new Set(
          sharedQuestions
            .map((q) => q.subject)
            .filter((subject) => subject && subject.trim() !== "")
        );

        response.data.forEach((subject) => {
          if (typeof subject === "string" && subject.trim() !== "") {
            questionSubjects.add(subject);
          } else if (subject && subject.name && subject.name.trim() !== "") {
            questionSubjects.add(subject.name);
          }
        });

        setSubjects(Array.from(questionSubjects));
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  const fetchComments = async (questionId) => {
    try {
      setLoadingComments(true);
      const response = await api.get(`/api/questions/${questionId}/comments/`);
      if (Array.isArray(response.data)) {
        setComments(response.data);
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("Error fetching comments:", error);
      setError("Failed to load comments. Please try again.");
    } finally {
      setLoadingComments(false);
    }
  };

  const handleViewQuestion = (question) => {
    if (!question || !question.id) {
      console.error("Attempted to view an invalid question:", question);
      setError("Cannot view this question. Invalid data.");
      return;
    }
    setSelectedQuestion(question);
    setShowViewModal(true);

    // Mark as viewed if possible
    try {
      // This assumes the share ID is available in the question data
      // You might need to adjust how you get the share ID based on your API
      if (question.share_id) {
        api.post(`/api/question-shares/${question.share_id}/mark-viewed/`);
      }
    } catch (error) {
      console.error("Error marking question as viewed:", error);
    }
  };

  const handleOpenComments = (question) => {
    if (!question || !question.id) {
      console.error(
        "Attempted to view comments for invalid question:",
        question
      );
      setError("Cannot view comments. Invalid data.");
      return;
    }
    setSelectedQuestion(question);
    setShowCommentModal(true);
    fetchComments(question.id);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedQuestion?.id) {
      return;
    }

    try {
      setSubmittingComment(true);
      const response = await api.post(`/api/question-comments/`, {
        question: selectedQuestion.id,
        comment: newComment,
      });

      // Add the new comment to the list
      if (response.data) {
        setComments([...comments, response.data]);
        setNewComment("");
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      setError("Failed to add comment. Please try again.");
    } finally {
      setSubmittingComment(false);
    }
  };

  // Filter questions based on search term and subject filter
  const filteredQuestions = sharedQuestions
    ? sharedQuestions.filter((question) => {
        const matchesSearch = searchTerm
          ? (question.title || "")
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            (question.subject || "")
              .toLowerCase()
              .includes(searchTerm.toLowerCase()) ||
            (question.level || "")
              .toLowerCase()
              .includes(searchTerm.toLowerCase())
          : true;

        const matchesSubject = subjectFilter
          ? (question.subject || "").toLowerCase() ===
            subjectFilter.toLowerCase()
          : true;

        return matchesSearch && matchesSubject;
      })
    : [];

  return (
    <Container className="my-4">
      <h2 className="mb-4">Questions Shared With Me</h2>

      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              placeholder="Search shared questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchTerm("")}
              >
                <FaTimes />
              </Button>
            )}
            <Button variant="outline-primary" disabled>
              <FaSearch />
            </Button>
          </InputGroup>
        </Col>
        <Col md={6}>
          <Form.Select
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
          >
            <option value="">All Subjects</option>
            {subjects.map((subject, index) => (
              <option key={index} value={subject}>
                {subject}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading shared questions...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : filteredQuestions.length === 0 ? (
        <Alert variant="info">
          <p className="mb-0">
            No shared questions found.
            {searchTerm || subjectFilter
              ? " Try adjusting your filters."
              : " No one has shared any questions with you yet."}
          </p>
        </Alert>
      ) : (
        <Table responsive hover>
          <thead>
            <tr>
              <th>Title</th>
              <th>Subject</th>
              <th>Level</th>
              <th>Shared By</th>
              <th>Solution</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredQuestions.map((question) => (
              <tr key={question?.id || `question-${Math.random()}`}>
                <td>{question?.title || "Untitled"}</td>
                <td>{question?.subject || "-"}</td>
                <td>{question?.level || "-"}</td>
                <td>{question?.username || "-"}</td>
                <td>
                  {question?.solution_source === "AI" && (
                    <Badge bg="info">AI Generated</Badge>
                  )}
                  {question?.solution_source === "USER" && (
                    <Badge bg="success">User Provided</Badge>
                  )}
                  {(!question?.solution_source ||
                    question.solution_source === "NONE") && (
                    <Badge bg="secondary">None</Badge>
                  )}
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="me-1"
                    onClick={() => handleViewQuestion(question)}
                  >
                    <FaEye />
                  </Button>
                  <Button
                    variant="outline-info"
                    size="sm"
                    onClick={() => handleOpenComments(question)}
                  >
                    <FaComment />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* View Question Modal */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {selectedQuestion?.title || "Question Details"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="question-details">
            <Row className="mb-3">
              <Col md={4}>
                <strong>Subject:</strong> {selectedQuestion?.subject || "-"}
              </Col>
              <Col md={4}>
                <strong>Level:</strong> {selectedQuestion?.level || "-"}
              </Col>
              <Col md={4}>
                <strong>Shared By:</strong> {selectedQuestion?.username || "-"}
              </Col>
            </Row>

            <Tabs defaultActiveKey="question" className="mb-3">
              <Tab eventKey="question" title="Question">
                {selectedQuestion?.question_body ? (
                  <MathJaxRenderer
                    content={selectedQuestion.question_body}
                    className="p-3 border rounded bg-light"
                  />
                ) : (
                  <p className="text-muted">No question content available.</p>
                )}
              </Tab>
              <Tab eventKey="solution" title="Solution">
                {selectedQuestion?.solution_body ? (
                  <MathJaxRenderer
                    content={selectedQuestion.solution_body}
                    className="p-3 border rounded bg-light"
                  />
                ) : (
                  <p className="text-muted">
                    No solution available for this question.
                  </p>
                )}
              </Tab>
            </Tabs>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowViewModal(false)}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowViewModal(false);
              handleOpenComments(selectedQuestion);
            }}
          >
            View Comments
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Comments Modal */}
      <Modal
        show={showCommentModal}
        onHide={() => setShowCommentModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Comments on {selectedQuestion?.title || "Question"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}

          <div className="comments-section">
            <h5 className="mb-3">Discussion</h5>

            {loadingComments ? (
              <div className="text-center my-4">
                <Spinner animation="border" size="sm" />
                <p className="mt-2">Loading comments...</p>
              </div>
            ) : comments.length === 0 ? (
              <Alert variant="light">
                No comments yet. Be the first to add a comment!
              </Alert>
            ) : (
              <div className="comment-list mb-4">
                {comments.map((comment) => (
                  <Card key={comment.id} className="mb-2">
                    <Card.Body>
                      <div className="d-flex justify-content-between">
                        <Card.Subtitle className="mb-2 text-muted">
                          {comment.username || "User"}
                        </Card.Subtitle>
                        <small className="text-muted">
                          {new Date(comment.created_at).toLocaleString()}
                        </small>
                      </div>
                      <Card.Text>{comment.comment}</Card.Text>
                      {comment.alternative_solution && (
                        <div className="mt-2">
                          <Card.Subtitle className="mb-2 text-primary">
                            Alternative Solution:
                          </Card.Subtitle>
                          <MathJaxRenderer
                            content={comment.alternative_solution}
                            className="p-2 border rounded bg-light"
                          />
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}

            <Form className="mt-4">
              <Form.Group>
                <Form.Label>Add a Comment</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Enter your comment here..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
              </Form.Group>
              <div className="d-flex justify-content-end mt-3">
                <Button
                  variant="primary"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || submittingComment}
                >
                  {submittingComment ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Submitting...
                    </>
                  ) : (
                    "Post Comment"
                  )}
                </Button>
              </div>
            </Form>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowCommentModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SharedQuestions;
